import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Copy, X, AlertCircle, ExternalLink, RefreshCw, Clock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { formatWithUTC } from '@/lib/datetime';

interface ErrorDetails {
  timestamp?: string;
  error_type?: string;
  stack_preview?: string;
  is_transient?: boolean;
}

interface Job {
  id: string;
  job_type: string;
  status: string;
  error_message?: string;
  error_details?: ErrorDetails | any;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  attempt_count?: number;
  max_attempts?: number;
}

interface ErrorLogPanelProps {
  job: Job;
  children: React.ReactNode;
}

export function ErrorLogPanel({ job, children }: ErrorLogPanelProps) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation('common');

  const copyErrorLog = async () => {
    if (job.error_message) {
      await navigator.clipboard.writeText(job.error_message);
      toast({
        title: t('errors.copied'),
        description: t('errors.copied.description')
      });
    }
  };

  const getFirstErrorLine = (errorMessage?: string) => {
    if (!errorMessage) return null;
    const firstLine = errorMessage.split('\n')[0];
    return firstLine.length > 60 ? `${firstLine.substring(0, 60)}...` : firstLine;
  };

  // Analyze error to determine if it's transient or permanent
  const analyzeError = () => {
    const msg = job.error_message?.toLowerCase() || '';
    const isTransient = 
      msg.includes('temporarily unavailable') ||
      msg.includes('429') ||
      msg.includes('503') ||
      msg.includes('timeout') ||
      msg.includes('econnreset') ||
      msg.includes('rate limit') ||
      msg.includes('this batch will be retried');
    
    return {
      isTransient,
      canRetry: isTransient && (job.attempt_count || 0) < (job.max_attempts || 3)
    };
  };

  const errorAnalysis = analyzeError();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {children}
      </SheetTrigger>
      <SheetContent className="min-w-[600px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-destructive" />
            {t('errors.report.title')}
          </SheetTitle>
          <SheetDescription>
            {t('errors.report.description')} {job.id.slice(0, 8)}...
          </SheetDescription>
        </SheetHeader>
        
        <div className="mt-6 space-y-6">
          {/* Job Info */}
          <div className="space-y-3">
            <h3 className="font-medium">{t('errors.job.info')}</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">{t('errors.job.id')}</span>
                <div className="font-mono">{job.id}</div>
              </div>
              <div>
                <span className="text-muted-foreground">{t('errors.job.type')}</span>
                <div>{job.job_type}</div>
              </div>
              <div>
                <span className="text-muted-foreground">{t('logs.created')}</span>
                <div>{formatWithUTC(job.created_at)}</div>
              </div>
              {job.started_at && (
                <div>
                  <span className="text-muted-foreground">{t('errors.job.started')}</span>
                  <div>{formatWithUTC(job.started_at)}</div>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Error Type Alert */}
          {errorAnalysis.isTransient ? (
            <Alert className="border-warning/50 bg-warning/5">
              <Clock className="h-4 w-4 text-warning" />
              <AlertTitle className="text-warning">Tillfälligt fel</AlertTitle>
              <AlertDescription>
                Detta fel är troligen tillfälligt och kan lösas genom att försöka igen.
                {errorAnalysis.canRetry && (
                  <span className="block mt-1 font-medium">
                    Försök {job.attempt_count || 0} av {job.max_attempts || 3} - kommer försökas igen automatiskt.
                  </span>
                )}
              </AlertDescription>
            </Alert>
          ) : (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Permanent fel</AlertTitle>
              <AlertDescription>
                Detta fel kräver manuell åtgärd. Kontrollera felet nedan och kontakta support om problemet kvarstår.
              </AlertDescription>
            </Alert>
          )}

          {/* Error Details */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">{t('errors.message.title')}</h3>
              <Button variant="outline" size="sm" onClick={copyErrorLog}>
                <Copy className="h-4 w-4 mr-2" />
                {t('errors.message.copy')}
              </Button>
            </div>
            
            {job.error_message ? (
              <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4">
                <pre className="text-sm whitespace-pre-wrap font-mono text-destructive">
                  {job.error_message}
                </pre>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground italic">
                {t('errors.message.none')}
              </div>
            )}

            {/* Additional error details if available */}
            {job.error_details && (
              <div className="space-y-2 text-sm">
                {job.error_details.error_type && (
                  <div>
                    <span className="text-muted-foreground">Feltyp: </span>
                    <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{job.error_details.error_type}</code>
                  </div>
                )}
                {job.error_details.timestamp && (
                  <div>
                    <span className="text-muted-foreground">Tidpunkt: </span>
                    <span>{formatWithUTC(job.error_details.timestamp)}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>
              <X className="h-4 w-4 mr-2" />
              {t('errors.close')}
            </Button>
            <Button variant="outline">
              <ExternalLink className="h-4 w-4 mr-2" />
              {t('errors.openLogViewer')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function ErrorBadge({ job }: { job: Job }) {
  if (job.status !== 'failed' || !job.error_message) return null;
  
  const firstLine = job.error_message.split('\n')[0];
  const shortError = firstLine.length > 40 ? `${firstLine.substring(0, 40)}...` : firstLine;
  
  // Determine if error is transient
  const msg = job.error_message.toLowerCase();
  const isTransient = 
    msg.includes('temporarily unavailable') ||
    msg.includes('429') ||
    msg.includes('503') ||
    msg.includes('timeout') ||
    msg.includes('rate limit');
  
  return (
    <ErrorLogPanel job={job}>
      <Badge 
        variant={isTransient ? "outline" : "destructive"}
        className={`cursor-pointer max-w-[200px] truncate ${
          isTransient 
            ? 'border-warning text-warning hover:bg-warning/10' 
            : 'hover:bg-destructive/20'
        }`}
        title={firstLine}
      >
        {isTransient ? <Clock className="h-3 w-3 mr-1" /> : <AlertCircle className="h-3 w-3 mr-1" />}
        {shortError}
      </Badge>
    </ErrorLogPanel>
  );
}